/**
 * StatusCard — shared aspect. White card with a status chip,
 * right-aligned date, Cormorant title, and a thin progress bar.
 * Used by /brand Aspect 17 AND by real surfaces (Up-Next-This-Week
 * on the Student program dashboard). Single source of truth.
 *
 * Tone semantics (resolved internally from PaneTheme):
 *   accent  — amber chip, amber progress fill (e.g. "In review")
 *   success — sage chip, dark progress fill (e.g. "Active")
 */
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";
import { RADII, SPACING, STROKES } from "../../lib/brand";

import type { PaneTheme } from "../brand/paneThemes";

export type StatusCardTone = "accent" | "success";

export type StatusCardProps = {
  theme: PaneTheme;
  tone: StatusCardTone;
  chipLabel: string;
  dateLabel: string;
  title: string;
  /** 0..1 */
  progress: number;
  onPress?: () => void;
  accessibilityLabel?: string;
};

function resolveTone(theme: PaneTheme, tone: StatusCardTone) {
  if (tone === "accent") {
    return {
      chipColor: theme.accent,
      chipBg: theme.accentTintSoft,
      chipBorder: theme.accentBorderSoft,
      fillColor: theme.accent,
    };
  }
  return {
    chipColor: theme.success,
    chipBg: theme.successTintSoft,
    chipBorder: theme.successBorderSoft,
    fillColor: theme.textOnSurface,
  };
}

export function StatusCard({
  theme,
  tone,
  chipLabel,
  dateLabel,
  title,
  progress,
  onPress,
  accessibilityLabel,
}: StatusCardProps) {
  const isWeb = Platform.OS === "web";
  const c = resolveTone(theme, tone);
  const pct = `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%` as `${number}%`;

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? "button" : undefined}
      accessibilityLabel={accessibilityLabel}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          backgroundColor: theme.surfaceRaisedBg,
          borderRadius: RADII.md,
          borderWidth: STROKES.hairline,
          borderColor:
            hovered && isWeb && onPress
              ? theme.accentBorderHover
              : theme.border,
          paddingVertical: 14,
          paddingHorizontal: SPACING.md,
        },
        isWeb
          ? ({
              cursor: onPress ? "pointer" : "default",
              transition: "border-color 0.18s ease, transform 0.18s ease",
              boxShadow:
                "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
              transform: hovered && onPress ? "translateY(-1px)" : "translateY(0)",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <View
          style={{
            paddingHorizontal: SPACING.xs,
            paddingVertical: 3,
            borderRadius: RADII.full,
            backgroundColor: c.chipBg,
            borderWidth: STROKES.hairline,
            borderColor: c.chipBorder,
          }}
        >
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 8.5,
              letterSpacing: 1.4,
              color: c.chipColor,
              textTransform: "uppercase",
            }}
          >
            {chipLabel}
          </Text>
        </View>
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 11.5,
            color: theme.accentDeep,
            fontVariant: ["tabular-nums"],
          }}
        >
          {dateLabel}
        </Text>
      </View>

      <Text
        numberOfLines={2}
        style={{
          fontFamily: theme.displayMediumFont,
          fontSize: 15,
          lineHeight: 18,
          color: theme.textOnSurface,
          marginBottom: 10,
        }}
      >
        {title}
      </Text>

      <View
        style={{
          height: 3,
          backgroundColor: theme.altBg,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            height: "100%",
            width: pct,
            backgroundColor: c.fillColor,
          }}
        />
      </View>
    </Pressable>
  );
}
